import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
} from '@mui/material';

export interface ResetDialogProps {
  open: boolean;
  currentValue: number;
  baseValue: number;
  onClose: () => void;
  onConfirm: () => void;
  includesUplift?: boolean;
}

/**
 * Shared dialog component for confirming override resets
 * Used across Unit Cost, Distribution, Rate of Sale, and SKU Base Forecast tables
 */
const ResetDialog: React.FC<ResetDialogProps> = ({
  open,
  currentValue,
  baseValue,
  onClose,
  onConfirm,
  includesUplift = false,
}) => {
  const numberFormatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 3,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Reset Override</DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {includesUplift 
            ? 'Are you sure you want to reset this override? This will remove both the rate override and any percent uplift.'
            : 'Are you sure you want to reset this to its original value?'}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 0.6 }}
                  color="text.secondary"
                >
                  Current
                </Typography>
                <Typography variant="h6" noWrap>
                  {numberFormatter.format(currentValue)}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{ letterSpacing: 0.6 }}
                  color="text.secondary"
                >
                  Original
                </Typography>
                <Typography variant="h6" color="text.secondary" noWrap>
                  {numberFormatter.format(baseValue)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" autoFocus>
          Reset
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetDialog;
